/**
 * WhatsAppDrawer — Persistent overlay for WhatsApp chat.
 * Mounted at Layout level, always in DOM. Visibility controlled by whatsappDrawerStore.
 * Two modes: 'full' (covers entire page) and 'compact' (right panel, 60% della finestra).
 * Quando lista e chat non stanno affiancate (sotto i 1024px, o compatto stretto)
 * si mostra una colonna sola: la lista, oppure la chat con la freccia per tornare.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MessageCircle, AlertTriangle } from 'lucide-react';
import { useUserData, useAuth } from '@/lib/auth';
import { useWhatsAppDrawerStore } from '@/lib/stores/whatsappDrawerStore';
import { useWhatsAppChatStore, clearWhatsAppChatCaches } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useWhatsAppConfigs } from '@/lib/hooks/useWhatsAppConfigs';
import { useApiConfig } from '@/lib/hooks/useApiConfig';
import { useWhatsAppRealtime } from '@/lib/hooks/whatsapp';
import { getWahaSessionStatus } from '@/lib/services/wahaProxyService';
import { type SessionStatus } from '@/lib/utils/whatsapp-setup';
import { ChatSidebar } from '@/components/whatsapp2/sidebar';
import { ChatView } from '@/components/whatsapp2/chat';
import { ChatDebugPanel } from '@/components/whatsapp2/chat/ChatDebugPanel';
import { SimpleWhatsAppSetup } from '@/components/whatsapp/SimpleWhatsAppSetup';
import { WhatsAppConnectionManager } from '@/components/whatsapp/WhatsAppConnectionManager';
import { DrawerHeader } from './DrawerHeader';
import { MetaInbox } from '@/components/meta-messaging/MetaInbox';
import { AllInbox } from '@/components/messages/AllInbox';
import { TeamChatPane } from '@/components/messages/TeamChatPane';
import { ChatTooltip } from '@/components/messages/ChatTooltip';
import { useMetaChannels } from '@/lib/hooks/useMetaChannels';
import { useAllConversations } from '@/lib/hooks/useAllConversations';
import { usePermissions } from '@/lib/hooks';
import { useSettingsStore } from '@/lib/stores/settingsStore';
import { useMetaMessagingStore } from '@/lib/stores/metaMessagingStore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { trovaChatPerNumero, isPlaceholderTarget } from '@/lib/services/whatsapp/chatMatch';
import { clearWhatsAppSessionKeys } from './whatsappSessionStorage';
import { isSingleColumnLayout, visibleColumns, compactDrawerWidth } from './drawerLayout';
import { useViewportWidth } from './useViewportWidth';
import { useCloseDrawerOnRouteChange } from './useCloseDrawerOnRouteChange';
import { useIncomingMessageNotifications } from './notifications/useIncomingMessageNotifications';
import { configErrorText } from './configErrorText';
import { NotificationToggle } from './notifications/NotificationToggle';

type InternalState = 'loading' | 'no-config' | 'config-error' | 'setup-needed' | 'connection' | 'chat';

export function WhatsAppDrawer() {
  const { isOpen, mode, close, isInitialized, markInitialized, pendingConfigId, pendingChatPhone, clearPendingConfig, clearPendingChat } = useWhatsAppDrawerStore();
  const { organizationId, userProfile } = useUserData();
  const navigate = useNavigate();

  // "Apri scheda" e gli altri link interni cambiano pagina: il drawer si chiude,
  // altrimenti la pagina nuova resta coperta.
  useCloseDrawerOnRouteChange(isOpen, close);

  // Canali Meta (Instagram, Messenger): stesso drawer, selettore in testa.
  const channel = useWhatsAppDrawerStore((s) => s.channel);
  const setChannel = useWhatsAppDrawerStore((s) => s.setChannel);
  const metaChannels = useMetaChannels();
  // Drawer unico "Messaggi" (decisione Luca 02/09/2026): Tutte | WhatsApp |
  // Instagram | Messenger | Team. WhatsApp e Team compaiono solo se l'utente ha
  // il permesso (e la chat di team e' attiva); Instagram e Messenger sempre, con
  // lo stato "non collegato" quando manca qualcosa.
  const { hasPermission } = usePermissions();
  const teamSettings = useSettingsStore((s) => s.settings);
  const whatsappVisible = hasPermission('whatsapp', 'view');
  const teamVisible = teamSettings?.team_chat_enabled === true && hasPermission('settings_team_chat', 'view');
  const channelsVisible = { whatsapp: whatsappVisible, team: teamVisible };
  const { counts: unifiedCounts } = useAllConversations({ includeTeam: teamVisible, includeWhatsApp: whatsappVisible });
  // Scheda non disponibile per questo utente: si torna a "Tutte".
  useEffect(() => {
    if ((channel === 'whatsapp' && !whatsappVisible) || (channel === 'team' && !teamVisible)) setChannel('all');
  }, [channel, whatsappVisible, teamVisible, setChannel]);
  const showMetaInbox = channel === 'instagram' || channel === 'messenger';
  // Conversazioni + realtime Meta appena i canali sono disponibili, anche col
  // drawer chiuso o sul canale WhatsApp: cosi' il badge "da rispondere" sul
  // selettore e' vero e non appare solo dopo aver aperto Instagram.
  const fetchMetaConversations = useMetaMessagingStore((s) => s.fetchConversations);
  const fetchMetaReads = useMetaMessagingStore((s) => s.fetchReads);
  const subscribeMetaRealtime = useMetaMessagingStore((s) => s.subscribeRealtime);
  useEffect(() => {
    if (!metaChannels.available) return;
    fetchMetaConversations().catch(() => undefined);
    fetchMetaReads().catch(() => undefined);
    return subscribeMetaRealtime();
  }, [metaChannels.available, fetchMetaConversations, fetchMetaReads, subscribeMetaRealtime]);

  // Esc chiude il drawer, ma solo se nessun componente interno (visualizzatore
  // media, ricerca, menzioni) ha gia' gestito il tasto.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[role="dialog"], [data-modal]')) return;
      // Dopo tutti gli altri ascoltatori: menu, emoji, anteprime e finestre aperte dentro
      // il pannello chiamano preventDefault per chiudere solo se stessi, ma essendo
      // registrati dopo questo verrebbero eseguiti dopo. Si decide a evento concluso.
      setTimeout(() => { if (!e.defaultPrevented) close(); }, 0);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Config state
  const { configs: availableConfigs, refetch: refetchConfigs } = useWhatsAppConfigs();
  const [activeConfigId, setActiveConfigId] = useState<string | null>(() => {
    const stored = localStorage.getItem('whatsapp_active_config');
    if (!stored || stored === 'undefined' || stored === 'null') return null;
    return stored;
  });

  const { wappConfig, loading: configLoading, error: configError, refetch: refetchConfig } = useApiConfig(activeConfigId);

  // Config attiva + flag Cloud. I numeri Cloud (whatsapp_cloud / whatsapp_cloud_coex)
  // NON hanno una sessione WAHA → vanno inizializzati bypassando i check WAHA
  // (sessione/connessione): le chat arrivano dal DB via store (CloudProvider).
  const activeConfig = useMemo(
    () => availableConfigs.find((c) => c.id === activeConfigId) || null,
    [availableConfigs, activeConfigId],
  );
  const isCloudActive = !!activeConfig && activeConfig.serviceName !== 'waha';

  // Internal state
  const [internalState, setInternalState] = useState<InternalState>('loading');
  const [isConnected, setIsConnected] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(null);
  const [sessionCheckComplete, setSessionCheckComplete] = useState(false);
  const [wahaConfig, setWahaConfig] = useState<{ apiUrl: string; apiKey: string; session: string } | null>(null);
  const sessionCheckAttemptsRef = useRef(0);
  const setupJustCompletedRef = useRef(false);

  // Store hooks
  const { loadConfig, loadChats, reset, selectedChatId, loadUnreadCountsFromDb } = useWhatsAppChatStore();
  const { user } = useAuth();

  // Realtime — attivo appena connesso, resta attivo anche col drawer chiuso
  useWhatsAppRealtime(isConnected ? organizationId ?? undefined : undefined);

  // Polling unread DB sempre attivo (drawer aperto o chiuso) → badge menu live.
  // Ogni 60s come fallback contro eventi realtime persi.
  useEffect(() => {
    if (!isConnected || !user?.id) return;
    loadUnreadCountsFromDb(user.id);
    const interval = setInterval(() => loadUnreadCountsFromDb(user.id), 60_000);
    return () => clearInterval(interval);
  }, [isConnected, user?.id, loadUnreadCountsFromDb]);

  // Badge globale senza le chat silenziate: servono le preferenze (muted_until) anche delle
  // chat con non letti mai viste nella lista (drawer chiuso). Si leggono solo quelle mancanti.
  const unreadWithoutMetaKey = useWhatsAppChatStore((s) =>
    s.chats
      .filter((c) => (c.unreadCount || 0) > 0 && !useWhatsAppChatMetaStore.getState().chatMeta.has(c.id))
      .map((c) => c.id)
      .sort()
      .join('|')
  );
  useEffect(() => {
    if (!isConnected || !organizationId || !unreadWithoutMetaKey) return;
    void useWhatsAppChatMetaStore.getState().fetchChatMeta(unreadWithoutMetaKey.split('|'), organizationId);
  }, [isConnected, organizationId, unreadWithoutMetaKey]);

  // Auto-select / reset config: se activeConfigId è assente OPPURE stale (config
  // cancellata — es. dopo un wipe → 406 su useApiConfig), seleziona la prima valida.
  useEffect(() => {
    if (availableConfigs.length === 0) return;
    const isValid = activeConfigId && availableConfigs.some((c) => c.id === activeConfigId);
    if (!isValid) {
      const first = availableConfigs[0];
      setActiveConfigId(first.id);
      localStorage.setItem('whatsapp_active_config', first.id);
      clearWhatsAppSessionKeys();
      refetchConfig();
    }
  }, [activeConfigId, availableConfigs, refetchConfig]);

  // Load wahaConfig from useApiConfig
  useEffect(() => {
    if (wappConfig?.session && wappConfig?.api_url) {
      setWahaConfig({ apiUrl: wappConfig.api_url, apiKey: wappConfig.api_key, session: wappConfig.session });
    }
  }, [wappConfig]);

  // Il pre-load parte qualche secondo dopo il mount: al login la pagina corrente
  // (kanban, dashboard) deve vincere la gara per le 6 connessioni del browser.
  // Se l'utente apre il drawer prima del timer, l'init parte subito.
  const [deferredInitReady, setDeferredInitReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDeferredInitReady(true), 6000);
    return () => clearTimeout(t);
  }, []);

  // Initialize appena la config è disponibile (NON aspetta l'apertura del drawer).
  // Realtime + chat list + unread polling restano attivi per tutta la sessione CRM,
  // così quando l'utente apre il drawer è già tutto pronto e il badge è live.
  useEffect(() => {
    if (isInitialized) return;
    if (!deferredInitReady && !isOpen) return;

    // ── CLOUD: init diretto. Nessuna sessione WAHA, nessuna attesa di useApiConfig.
    //    Le chat arrivano dal DB via store (CloudProvider). ──
    if (isCloudActive && organizationId) {
      markInitialized();
      setIsConnected(true);
      setInternalState('loading');
      loadConfig(organizationId, userProfile?.id, activeConfigId || undefined).then(() => {
        loadChats();
        setInternalState('chat');
        setSessionCheckComplete(true);
        setSessionStatus('WORKING');
        useWhatsAppAgentStore.getState().fetchAssignments(organizationId);
      }).catch(() => {
        setInternalState('setup-needed');
        setSessionCheckComplete(true);
      });
      return;
    }

    if (configLoading) return;

    if (wappConfig?.session && organizationId) {
      markInitialized();
      // Carica config e chat direttamente
      setIsConnected(true);
      setInternalState('loading');
      loadConfig(organizationId, userProfile?.id, activeConfigId || undefined).then(() => {
        loadChats();
        setInternalState('chat');
        setSessionCheckComplete(true);
        setSessionStatus('WORKING');
        // Agent store
        const agentStore = useWhatsAppAgentStore.getState();
        agentStore.fetchAssignments(organizationId);
        const cfgId = useWhatsAppChatStore.getState().wahaConfig?.configId;
        if (cfgId) agentStore.smartAutoAnalyze(organizationId, cfgId);
      }).catch(() => {
        setInternalState('setup-needed');
        setSessionCheckComplete(true);
      });
    } else if (!configLoading && availableConfigs.length === 0) {
      markInitialized();
      setInternalState('no-config');
      setSessionCheckComplete(true);
    }
  }, [isInitialized, deferredInitReady, isOpen, configLoading, configError, wappConfig, availableConfigs.length, organizationId, userProfile?.id, activeConfigId, markInitialized, loadConfig, loadChats, isCloudActive]);

  // Session check
  useEffect(() => {
    // ── CLOUD: nessun session check WAHA. Connetti direttamente + chat dal DB.
    //    Gestisce anche il cambio numero WAHA→Cloud (handleSessionSwitch azzera isConnected). ──
    if (isCloudActive) {
      if (organizationId && !isConnected) {
        setIsConnected(true);
        setSessionStatus('WORKING');
        setSessionCheckComplete(true);
        loadConfig(organizationId, userProfile?.id, activeConfigId || undefined).then(() => {
          loadChats();
          useWhatsAppAgentStore.getState().fetchAssignments(organizationId);
        });
      }
      setInternalState('chat');
      return;
    }
    if (!isInitialized || !wappConfig?.session) return;
    if (setupJustCompletedRef.current) return;
    if (configLoading) { setInternalState('loading'); return; }
    if (configError) {
      // Dettaglio tecnico solo in console: all'utente una frase italiana fissa (configErrorText).
      console.warn('[WhatsAppDrawer] lettura configurazione non riuscita:', configError);
      // Errore nel leggere la configurazione: si mostra l'errore, NON la
      // procedura "Configura WhatsApp" (il numero esiste gia').
      // sessionCheckComplete resta com'e': quando l'errore sparisce (Riprova,
      // numero cambiato) il controllo della sessione riparte da capo.
      setInternalState('config-error');
      return;
    }

    if (!sessionCheckComplete) {
      // Do session check
      if (sessionCheckAttemptsRef.current >= 3) {
        setSessionStatus('FAILED');
        setSessionCheckComplete(true);
        return;
      }
      sessionCheckAttemptsRef.current += 1;

      getWahaSessionStatus(wappConfig.session).then((status) => {
        setSessionStatus(status as SessionStatus);
        setSessionCheckComplete(true);
      }).catch(() => {
        setSessionStatus('FAILED');
        setSessionCheckComplete(true);
      });
      setInternalState('loading');
      return;
    }

    if (sessionStatus === 'NOT_FOUND') { setInternalState('setup-needed'); return; }

    if (sessionStatus === 'WORKING') {
      if (!isConnected) {
        setIsConnected(true);
        if (organizationId) {
          loadConfig(organizationId, userProfile?.id, activeConfigId || undefined).then(() => {
            loadChats();
            const agentStore = useWhatsAppAgentStore.getState();
            agentStore.fetchAssignments(organizationId);
            agentStore.fetchPendingSuggestionCounts(organizationId);
            agentStore.fetchChatSummaries(organizationId);
            const cfgId = useWhatsAppChatStore.getState().wahaConfig?.configId;
            if (cfgId) agentStore.smartAutoAnalyze(organizationId, cfgId);
          });
        }
      }
      setInternalState('chat');
      return;
    }

    setInternalState('connection');
  }, [isInitialized, configLoading, configError, wappConfig, sessionCheckComplete, sessionStatus, isConnected, organizationId, userProfile?.id, activeConfigId, loadConfig, loadChats, availableConfigs.length, isCloudActive]);

  // Session switch
  const handleSessionSwitch = useCallback((newConfigId: string) => {
    if (!organizationId) return;
    setActiveConfigId(newConfigId);
    localStorage.setItem('whatsapp_active_config', newConfigId);
    // Finche' il nuovo numero non e' pronto la vista non deve restare "chat" del numero vecchio.
    setInternalState('loading');

    setIsConnected(false);
    setSessionStatus(null);
    setSessionCheckComplete(false);
    sessionCheckAttemptsRef.current = 0;
    setupJustCompletedRef.current = false;

    clearWhatsAppSessionKeys();
    clearWhatsAppChatCaches();
    reset();
    refetchConfig();
  }, [organizationId, reset, refetchConfig]);

  // Richiesta esterna di config specifica (openWithChat dalla card kanban):
  // switch solo se diversa dall'attiva; consumata una volta note le config.
  useEffect(() => {
    if (!pendingConfigId || availableConfigs.length === 0) return;
    const exists = availableConfigs.some((c) => c.id === pendingConfigId);
    if (exists && pendingConfigId !== activeConfigId) {
      handleSessionSwitch(pendingConfigId);
    }
    if (!exists) {
      // Numero non piu' accessibile (accesso revocato, card di cronologia vecchia):
      // non aprire la chat sul numero attivo facendo credere che sia quello giusto.
      clearPendingChat();
      toast.error('Questo numero WhatsApp non e\u0300 piu\u0300 disponibile per il tuo utente');
    }
    clearPendingConfig();
  }, [pendingConfigId, availableConfigs, activeConfigId, handleSessionSwitch, clearPendingConfig, clearPendingChat]);

  // Richiesta esterna di chat (numero o JID): risolta quando la lista chat di
  // QUESTO numero aziendale e' arrivata (chatsLoadedForConfig): dopo uno switch
  // la lista vecchia non conta. Se la chat non e' nella prima pagina (WAHA ne da'
  // 100 per volta) si cerca nella cache completa; se col numero non si e' mai
  // parlato si crea una bozza locale col nome del lead, come fa la barra
  // laterale da una ricerca. Per gruppi e @lid non si inventa niente.
  const pendingChats = useWhatsAppChatStore((s) => s.chats);
  const pendingChatsLoading = useWhatsAppChatStore((s) => s.chatsLoading);
  const chatsLoadedForConfig = useWhatsAppChatStore((s) => s.chatsLoadedForConfig);
  const storeConfigId = useWhatsAppChatStore((s) => s.wahaConfig?.configId ?? null);
  const pendingChatName = useWhatsAppDrawerStore((s) => s.pendingChatName);
  const pendingChatBusy = useRef(false);
  useEffect(() => {
    if (!pendingChatPhone || pendingConfigId) return;
    if (internalState !== 'chat' || pendingChatsLoading) return;
    if (!storeConfigId || chatsLoadedForConfig !== storeConfigId) return;
    if (pendingChatBusy.current) return;
    const target = pendingChatPhone;
    const store = useWhatsAppChatStore.getState();
    const trovata = trovaChatPerNumero(store.chats, target);
    if (trovata) {
      store.selectChat(trovata.id);
      clearPendingChat();
      return;
    }
    pendingChatBusy.current = true;
    (async () => {
      try {
        if (store.chatsHasMore) {
          await store.loadAllChatsCache();
          const inCache = trovaChatPerNumero(useWhatsAppChatStore.getState().allChatsCache || [], target);
          if (inCache) {
            const s2 = useWhatsAppChatStore.getState();
            s2.addChatToStore({ id: inCache.id, name: inCache.name, isGroup: inCache.isGroup, pinned: false, archived: false });
            s2.selectChat(inCache.id);
            return;
          }
        }
        if (!isPlaceholderTarget(target)) {
          toast.error('Conversazione non trovata su questo numero WhatsApp');
          return;
        }
        const id = `${target}@c.us`;
        const s3 = useWhatsAppChatStore.getState();
        s3.addChatToStore({ id, name: pendingChatName || `+${target}`, isGroup: false, pinned: false, archived: false, isDraft: true });
        s3.selectChat(id);
      } finally {
        pendingChatBusy.current = false;
        clearPendingChat();
      }
    })();
  }, [pendingChatPhone, pendingChatName, pendingConfigId, internalState, pendingChats, pendingChatsLoading, chatsLoadedForConfig, storeConfigId, clearPendingChat]);

  // Numero scelto ma non connesso (o da configurare): la richiesta non deve
  // restare appesa e saltare fuori giorni dopo. Si avvisa e si lascia scegliere.
  useEffect(() => {
    if (!pendingChatPhone) return;
    if (internalState !== 'connection' && internalState !== 'setup-needed' && internalState !== 'no-config') return;
    const cfg = availableConfigs.find((c) => c.id === activeConfigId);
    const nome = cfg?.displayName || cfg?.phoneNumber || 'scelto';
    toast.error(`Il numero ${nome} non e\u0300 connesso: riconnettilo o scegli un altro numero`);
    clearPendingChat();
  }, [pendingChatPhone, internalState, availableConfigs, activeConfigId, clearPendingChat]);

  // Setup complete handler
  const handleSetupComplete = useCallback(() => {
    setupJustCompletedRef.current = true;
    setIsConnected(true);
    setSessionStatus('WORKING');
    setSessionCheckComplete(true);
    setInternalState('chat');

    if (organizationId) {
      const attempt = async (n = 1) => {
        try {
          await loadConfig(organizationId, userProfile?.id, activeConfigId || undefined);
          const cfgId = useWhatsAppChatStore.getState().wahaConfig?.configId;
          if (cfgId) {
            localStorage.setItem('whatsapp_active_config', cfgId);
            setActiveConfigId(cfgId);
          }
          await loadChats();
          setupJustCompletedRef.current = false;
          refetchConfig();
          refetchConfigs();
        } catch {
          if (n < 3) setTimeout(() => attempt(n + 1), 2000);
        }
      };
      setTimeout(() => attempt(), 2000);
    }
  }, [organizationId, userProfile?.id, activeConfigId, loadConfig, loadChats, refetchConfig, refetchConfigs]);

  const handleConnected = useCallback(() => {
    setIsConnected(true);
    if (organizationId) {
      loadConfig(organizationId, userProfile?.id, activeConfigId || undefined).then(() => {
        loadChats();
      });
    }
    setInternalState('chat');
  }, [organizationId, userProfile?.id, activeConfigId, loadConfig, loadChats]);

  // Avvisi per i nuovi messaggi (suono + notifica del browser su permesso esplicito).
  const notifications = useIncomingMessageNotifications(user?.id);

  // Impaginazione: una colonna sola quando lista e chat non stanno affiancate.
  const viewportWidth = useViewportWidth();
  const singleColumn = isSingleColumnLayout(mode, viewportWidth);
  const columns = visibleColumns(singleColumn, !!selectedChatId);
  const narrowHeader = mode === 'compact' ? compactDrawerWidth(viewportWidth) < 720 : viewportWidth < 640;
  const handleBackToList = useCallback(() => {
    useWhatsAppChatStore.getState().selectChat(null);
  }, []);

  // Animation: keep mounted during exit transition
  const [shouldRender, setShouldRender] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Blocca scroll su body, html, main e layout content area
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      const main = document.querySelector('main');
      if (main) main.style.overflow = 'hidden';
      const layoutContent = document.querySelector('.layout-content-area');
      if (layoutContent) (layoutContent as HTMLElement).style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
    } else {
      setAnimating(false);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      const main = document.querySelector('main');
      if (main) main.style.overflow = '';
      const layoutContent = document.querySelector('.layout-content-area');
      if (layoutContent) (layoutContent as HTMLElement).style.overflow = '';
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      const main = document.querySelector('main');
      if (main) main.style.overflow = '';
      const layoutContent = document.querySelector('.layout-content-area');
      if (layoutContent) (layoutContent as HTMLElement).style.overflow = '';
    };
  }, [isOpen]);

  // Non fare return null — il realtime hook DEVE restare montato
  // Nascondiamo visivamente il drawer quando chiuso
  if (!shouldRender) {
    return <div className="hidden" aria-hidden="true" />;
  }

  // Content based on internal state
  let content: React.ReactNode;

  if (internalState === 'loading' || (!isInitialized && configLoading)) {
    content = (
      <div className="flex-1 flex items-center justify-center bg-[#FBFBFA] dark:bg-[#191919]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#E91E8C] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#787774]">
            {pendingChatPhone
              ? `Apertura chat con ${pendingChatName || `+${pendingChatPhone.split('@')[0]}`}…`
              : 'Caricamento WhatsApp...'}
          </p>
        </div>
      </div>
    );
  } else if (internalState === 'config-error') {
    content = (
      <div className="flex-1 flex items-center justify-center bg-[#FBFBFA] dark:bg-[#191919] p-6">
        <div role="alert" className="max-w-md text-center">
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm font-semibold text-[#37352F] dark:text-white">Non riesco a leggere la configurazione di questo numero WhatsApp</p>
          <p className="text-xs text-[#5F5E5B] dark:text-white/70 mt-1">
            {configErrorText(configError)}
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <button onClick={() => refetchConfig()} className="px-3 py-1.5 text-xs font-semibold text-white bg-[#2383E2] hover:bg-[#1a6fc2] rounded-md">
              Riprova
            </button>
            <button onClick={() => { close(); navigate('/settings?tab=whatsapp-config'); }}
              className="px-3 py-1.5 text-xs font-semibold text-[#37352F] dark:text-white border border-[rgba(55,53,47,0.16)] dark:border-white/15 hover:bg-[rgba(55,53,47,0.06)] rounded-md">
              Apri Impostazioni WhatsApp
            </button>
          </div>
        </div>
      </div>
    );
  } else if (internalState === 'no-config' && organizationId) {
    content = (
      <div className="flex-1 overflow-y-auto bg-[#FBFBFA] dark:bg-[#191919]">
        <SimpleWhatsAppSetup
          organizationId={organizationId}
          skipCleanup={true}
          onComplete={handleSetupComplete}
        />
      </div>
    );
  } else if (internalState === 'setup-needed' && organizationId) {
    content = (
      <div className="flex-1 overflow-y-auto bg-[#FBFBFA] dark:bg-[#191919]">
        <SimpleWhatsAppSetup
          organizationId={organizationId}
          existingConfigId={wappConfig?.id || activeConfigId}
          skipCleanup={true}
          onComplete={handleSetupComplete}
        />
      </div>
    );
  } else if (internalState === 'connection' && wahaConfig) {
    content = (
      <div className="flex-1 overflow-y-auto bg-[#FBFBFA] dark:bg-[#191919]">
        <WhatsAppConnectionManager
          wahaUrl={wahaConfig.apiUrl}
          wahaApiKey={wahaConfig.apiKey}
          sessionName={wahaConfig.session}
          onConnected={handleConnected}
          onReconfigureNeeded={() => {
            clearWhatsAppSessionKeys();
            setIsConnected(false);
            setSessionStatus('NOT_FOUND');
            setSessionCheckComplete(true);
            sessionCheckAttemptsRef.current = 0;
          }}
        />
      </div>
    );
  } else if (internalState === 'chat') {
    // Due colonne affiancate (lista + chat) oppure una sola: la lista, o la chat
    // aperta con la freccia che riporta alla lista.
    const listWidth = mode === 'full' ? 'w-[400px]' : 'w-[370px]';
    content = (
      <div className="flex-1 flex overflow-hidden bg-[#FBFBFA] dark:bg-[#191919]">
        {columns !== 'chat' && (
          <div className={`${columns === 'list' ? 'flex-1 min-w-0' : `${listWidth} flex-shrink-0 border-r border-[rgba(55,53,47,0.09)] dark:border-white/10`} h-full flex flex-col overflow-hidden`}>
            <ChatSidebar />
          </div>
        )}
        {columns !== 'list' && (
          <div className="flex-1 h-full min-w-0 overflow-hidden">
            <ChatView
              compactMode={mode === 'compact' || singleColumn}
              onBack={singleColumn ? handleBackToList : undefined}
            />
          </div>
        )}
        <ChatDebugPanel />
      </div>
    );
  } else {
    content = (
      <div className="flex-1 flex items-center justify-center bg-[#FBFBFA] dark:bg-[#191919]">
        <div className="text-center">
          <MessageCircle className="h-10 w-10 text-[#787774] mx-auto mb-3" />
          <p className="text-sm text-[#787774]">Configura WhatsApp dalle impostazioni</p>
        </div>
      </div>
    );
  }

  // Inbox Meta: sostituisce il contenuto WhatsApp qualunque sia lo stato della
  // sessione WAHA (un'org puo' avere Instagram senza un numero WhatsApp).
  if (showMetaInbox && (channel === 'instagram' || channel === 'messenger')) {
    content = <MetaInbox channel={channel} compact={mode === 'compact'} onClose={close} />;
  } else if (channel === 'all') {
    content = (
      <AllInbox
        compact={mode === 'compact'}
        includeTeam={teamVisible}
        includeWhatsApp={whatsappVisible}
        whatsappReady={internalState === 'chat'}
      />
    );
  } else if (channel === 'team') {
    content = <TeamChatPane onClose={close} />;
  }

  const headerProps = {
    configs: availableConfigs,
    activeConfigId,
    onSelectSession: handleSessionSwitch,
    onAddSession: () => { close(); navigate('/settings?tab=whatsapp-config'); },
    channelsVisible,
    narrow: narrowHeader,
    notificationControl: (
      <NotificationToggle
        enabled={notifications.enabled}
        onToggle={notifications.setEnabled}
        permission={notifications.permission}
        onRequestPermission={() => { void notifications.requestBrowserPermission(); }}
      />
    ),
    pendingByChannel: {
      all: unifiedCounts.all,
      whatsapp: unifiedCounts.whatsapp,
      instagram: metaChannels.available ? unifiedCounts.instagram : 0,
      messenger: metaChannels.available ? unifiedCounts.messenger : 0,
      team: unifiedCounts.team,
    },
  };

  // Full mode — riempie l'area contenuto (rispetta sidebar e header del layout)
  if (mode === 'full') {
    return (
      <div
        className={`fixed z-[64] flex flex-col bg-white dark:bg-[#191919] rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden transition-all duration-250 ease-out ${
          animating ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97]'
        }`}
        style={{
          left: 'calc(var(--sidebar-width, 256px) + 8px)',
          top: 'calc(3rem + 8px)',
          right: '8px',
          bottom: '8px',
          transformOrigin: 'left center',
        }}
      >
        <DrawerHeader {...headerProps} />
        <div key={channel} className="flex-1 flex min-h-0 flex-col animate-fade-in">{content}</div>
        <ChatTooltip />
      </div>
    );
  }

  // Compact mode — drawer attaccato a destra come TeamChat
  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[63] bg-black/20 dark:bg-black/40 transition-opacity duration-250 max-md:!left-0 ${
          animating ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ top: '3rem', left: 'var(--sidebar-width, 256px)' }}
        onClick={close}
      />
      {/* Drawer */}
      <div
        className={`fixed right-0 z-[64] w-[60%] min-w-[420px] max-md:w-full max-md:min-w-0 flex flex-col bg-white dark:bg-[#191919] border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 shadow-2xl overflow-hidden transition-transform duration-300 ease-in-out ${
          animating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: '3rem', bottom: 0 }}
      >
        <DrawerHeader {...headerProps} />
        <div key={channel} className="flex-1 flex min-h-0 flex-col animate-fade-in">{content}</div>
        <ChatTooltip />
      </div>
    </>
  );
}
